import { useState, type ReactElement, type ReactNode } from 'react';
import { Badge } from '../../components/Badge';
import type { PlanTrack } from '../../types/learning-plans';
import {
  availableSubjects, composeCompensation, parseCompensation,
  type CompensationBasis, type OfferSubject,
} from './offer-setup';

const MODEL_LABEL: Record<PlanTrack['model'], string> = { generalist: 'معلّم شامل', specialist: 'معلّم متخصص', quran: 'القرآن' };
const DURATIONS = [30, 45, 60, 90, 120];

/** A pill whose real input is a radio or checkbox, so keyboard and screen readers work unchanged. */
const pill =
  'relative inline-flex min-w-10 cursor-pointer items-center justify-center rounded-md border border-rule px-3 py-1.5 text-sm tabular-nums ' +
  'hover:border-rule-strong has-[:checked]:border-accent has-[:checked]:bg-accent-wash has-[:checked]:font-semibold has-[:checked]:text-accent ' +
  'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent';

/**
 * One teaching track of an offer: who teaches what, how often, for how long,
 * and what the teacher is paid. Each setting is a single choice the operator
 * can see at a glance instead of a field to type into.
 */
export function OfferTrackEditor({ track, index, subjects, currency, canRemove, shared, onChange, onRemove, onShareCompensation }: {
  track: PlanTrack;
  index: number;
  subjects: OfferSubject[];
  currency: string;
  canRemove: boolean;
  shared: boolean;
  onChange: (value: Partial<PlanTrack>) => void;
  onRemove: () => void;
  onShareCompensation: () => void;
}): ReactElement {
  const choices = availableSubjects(track.model, subjects);
  const [search, setSearch] = useState('');
  const parsed = parseCompensation(track.compensation);
  // Custom wording is kept as typed; a fresh or composed sentence starts in the simple form.
  const [custom, setCustom] = useState(Boolean(track.compensation.trim()) && !parsed);
  const [typedAmount, setAmount] = useState(parsed?.amount ?? '');
  const [typedBasis, setBasis] = useState<CompensationBasis>(parsed?.basis ?? 'lesson');
  // Another track can share its pay into this one; the stored sentence wins
  // over what was typed here whenever the two disagree.
  const external = parsed && composeCompensation(typedAmount, typedBasis, currency) !== track.compensation;
  const amount = external ? parsed.amount : typedAmount;
  const basis = external ? parsed.basis : typedBasis;
  const pay = (nextAmount: string, nextBasis: CompensationBasis): void => {
    setAmount(nextAmount); setBasis(nextBasis);
    onChange({ compensation: composeCompensation(nextAmount, nextBasis, currency) });
  };
  const shown = choices.filter((subject) => `${subject.nameEn} ${subject.nameAr ?? ''}`.toLowerCase().includes(search.trim().toLowerCase()));
  const durations = DURATIONS.includes(track.durationMinutes) ? DURATIONS : [...DURATIONS, track.durationMinutes].sort((a, b) => a - b);

  return (
    <section className="min-w-0 rounded-lg border border-rule" aria-label={`المسار ${index + 1} · ${track.name}`}>
      <header className="flex flex-wrap items-center justify-between gap-2 rounded-t-lg bg-surface-2 px-4 py-3">
        <div className="flex items-center gap-2">
          <span aria-hidden="true" className="inline-flex size-6 items-center justify-center rounded-full bg-surface text-xs tabular-nums text-ink-2">{index + 1}</span>
          <h4 className="font-semibold">{track.name}</h4>
          <Badge>{MODEL_LABEL[track.model]}</Badge>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-ink-2 tabular-nums">
            {track.weeklyMeetings} أسبوعيًا · {track.meetings} حصة · {track.durationMinutes} دقيقة
          </span>
          {canRemove && (
            <button type="button" className="btn btn-ghost btn-sm text-danger" aria-label={`حذف المسار ${track.name}`} onClick={onRemove}>
              حذف
            </button>
          )}
        </div>
      </header>

      <div className="space-y-5 p-4">
        {track.model === 'generalist' ? (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">المواد المشمولة · {track.subjectIds.length} محدد</legend>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-ink-3">يدرّسها معلّم واحد. الإنجليزية في مسار مستقل، والقرآن في خطة مستقلة.</p>
              <div className="flex gap-1">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange({ subjectIds: [...new Set([...track.subjectIds, ...shown.map((s) => s.id)])] })}>تحديد الكل</button>
                <button type="button" className="btn btn-ghost btn-sm" disabled={!track.subjectIds.length} onClick={() => onChange({ subjectIds: [] })}>مسح</button>
              </div>
            </div>
            {choices.length > 12 && (
              <input type="search" className="input" value={search} placeholder="ابحث عن مادة" aria-label="البحث عن مادة"
                onChange={(event) => setSearch(event.target.value)} />
            )}
            <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto">
              {shown.map((subject) => (
                <label key={subject.id} className={pill}>
                  <input type="checkbox" className="sr-only" checked={track.subjectIds.includes(subject.id)}
                    onChange={(event) => onChange({ subjectIds: event.target.checked
                      ? [...track.subjectIds, subject.id] : track.subjectIds.filter((id) => id !== subject.id) })} />
                  {subject.nameAr || subject.nameEn}
                </label>
              ))}
              {shown.length === 0 && <p className="text-sm text-ink-3">لا توجد مادة بهذا الاسم.</p>}
            </div>
          </fieldset>
        ) : (
          <label className="block text-sm font-medium">المادة
            <select className="input mt-1" value={track.subjectIds[0] ?? ''} onChange={(event) => {
              const subject = subjects.find((candidate) => candidate.id === event.target.value);
              onChange({ subjectIds: subject ? [subject.id] : [], name: subject?.nameAr || subject?.nameEn || track.name });
            }}>
              <option value="">اختر المادة</option>
              {choices.map((subject) => <option key={subject.id} value={subject.id}>{subject.nameAr || subject.nameEn}</option>)}
            </select>
          </label>
        )}

        <Choice legend="الحصص الأسبوعية" hint={`${track.meetings} حصة خلال الدورة (4 أسابيع)`}>
          {[1, 2, 3, 4, 5, 6, 7].map((n) => (
            <label key={n} className={pill}>
              <input type="radio" className="sr-only" name={`weekly-${track.key}`} checked={track.weeklyMeetings === n}
                onChange={() => onChange({ weeklyMeetings: n, meetings: n * 4 })} />
              {n}
            </label>
          ))}
        </Choice>

        <Choice legend="مدة الحصة بالدقائق" hint={track.model === 'quran' ? 'يمكن أن تمتد حصة القرآن حتى المدة القصوى أدناه.' : undefined}>
          {durations.map((minutes) => (
            <label key={minutes} className={pill}>
              <input type="radio" className="sr-only" name={`duration-${track.key}`} checked={track.durationMinutes === minutes}
                onChange={() => onChange({ durationMinutes: minutes,
                  maxDurationMinutes: track.model === 'quran' ? Math.max(minutes, track.maxDurationMinutes) : minutes })} />
              {minutes}
            </label>
          ))}
        </Choice>
        {track.model === 'quran' && (
          <label className="block text-sm">أقصى مدة للحصة بالدقائق
            <input className="input mt-1 w-32" type="number" min={track.durationMinutes} max={120} value={track.maxDurationMinutes}
              onChange={(event) => onChange({ maxDurationMinutes: Number(event.target.value) })} />
          </label>
        )}

        <fieldset className="space-y-2 rounded-lg border border-rule p-3">
          <legend className="px-1 text-sm font-medium">مستحقات المعلّم</legend>
          {custom ? (
            <label className="block text-sm">شروط مستحقات المعلّم
              <textarea className="input mt-1" rows={2} value={track.compensation} maxLength={1000}
                placeholder="اكتب المبلغ وطريقة احتسابه وموعد التسوية" onChange={(event) => onChange({ compensation: event.target.value })} />
            </label>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm">مبلغ مستحقات المعلّم ({currency})
                <input className="input mt-1 w-32" type="number" min={0} step="0.01" inputMode="decimal" value={amount}
                  onChange={(event) => pay(event.target.value, basis)} />
              </label>
              <div role="radiogroup" aria-label="طريقة الاحتساب" className="flex gap-2">
                {(['lesson', 'cycle'] as const).map((value) => (
                  <label key={value} className={pill}>
                    <input type="radio" className="sr-only" name={`basis-${track.key}`} checked={basis === value} onChange={() => pay(amount, value)} />
                    {value === 'lesson' ? 'لكل حصة' : 'للدورة كاملة'}
                  </label>
                ))}
              </div>
            </div>
          )}
          {track.compensation.trim()
            ? <p className="text-xs text-ink-2">سيرى المعلّم: «{track.compensation}»</p>
            : <p className="text-xs text-ink-3">يظهر للمعلّم قبل قبول الطلب، ولا تراه الأسرة.</p>}
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => {
              if (custom) pay(amount, basis);
              setCustom(!custom);
            }}>{custom ? 'استخدام المبلغ وطريقة الاحتساب' : 'كتابة شروط مخصصة'}</button>
            {shared && (
              <button type="button" className="btn btn-ghost btn-sm" disabled={!track.compensation.trim()} onClick={onShareCompensation}>
                استخدام هذه المستحقات لكل المعلّمين
              </button>
            )}
          </div>
        </fieldset>

        <details className="rounded-lg border border-rule p-3">
          <summary className="cursor-pointer text-sm">اسم المسار والتجربة والتعديلات (اختياري)</summary>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <label className="text-sm">اسم العرض<input className="input mt-1" value={track.name} maxLength={120} onChange={(event) => onChange({ name: event.target.value })} /></label>
            <label className="text-sm">مدة التجربة بالدقائق<input className="input mt-1" type="number" min={30} max={120} value={track.trialDurationMinutes} onChange={(event) => onChange({ trialDurationMinutes: Number(event.target.value) })} /></label>
            <label className="text-sm">تعديلات الطالب خلال الدورة<input className="input mt-1" type="number" min={0} max={10} value={track.changeAllowance} onChange={(event) => onChange({ changeAllowance: Number(event.target.value) })} /></label>
          </div>
        </details>
      </div>
    </section>
  );
}

function Choice({ legend, hint, children }: { legend: string; hint?: string; children: ReactNode }): ReactElement {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">{legend}</legend>
      <div className="flex flex-wrap gap-2">{children}</div>
      {hint && <p className="text-xs text-ink-3">{hint}</p>}
    </fieldset>
  );
}
